import { create } from 'zustand';

/**
 * Toasts.
 *
 * A toast reports the outcome of something the operator started and then looked
 * away from — a job queued, a tag deleted. It is never the only place an
 * outcome appears: anything that matters is also visible in the thing it
 * changed, because a notification that has already faded is not evidence.
 *
 * Screens call `toast.success(...)`; `<Toaster />` (components/ui) draws them.
 */

export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  readonly id: number;
  readonly tone: ToastTone;
  readonly message: string;
  readonly detail?: string | undefined;
}

interface ToastState {
  readonly toasts: readonly Toast[];
  show(tone: ToastTone, message: string, detail?: string): void;
  dismiss(id: number): void;
}

// Errors stay longer: they usually carry something worth reading twice.
const LIFETIME: Record<ToastTone, number> = { success: 3500, info: 4000, error: 7000 };

let sequence = 0;

export const useToastStore = create<ToastState>()((set, get) => ({
  toasts: [],
  show(tone, message, detail) {
    sequence += 1;
    const id = sequence;
    // Three at most: a fourth pushes the oldest out.
    set((state) => ({ toasts: [...state.toasts.slice(-2), { id, tone, message, detail }] }));
    setTimeout(() => get().dismiss(id), LIFETIME[tone]);
  },
  dismiss(id) {
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
  },
}));

/** The calls screens make. Usable anywhere, including outside components. */
export const toast = {
  success: (message: string, detail?: string): void => useToastStore.getState().show('success', message, detail),
  error: (message: string, detail?: string): void => useToastStore.getState().show('error', message, detail),
  info: (message: string, detail?: string): void => useToastStore.getState().show('info', message, detail),
};
